import { API } from 'aws-amplify';

const URL_BASE = 'https://api.123cellphonesolutions.com/api/';
//const URL_BASE = 'http://localhost:32796/backoffice/api/';

export const ErrorCodes = {
    CLIENT_ERROR: 'CLIENT_ERROR',        //400-499  Any non-specific 400 series error.
    SERVER_ERROR: 'SERVER_ERROR',        //500-599  Any 500 series error.
    TIMEOUT_ERROR: 'TIMEOUT_ERROR',      //--- Server didn't respond in time.
    CONNECTION_ERROR: 'CONNECTION_ERROR',//--- Server not available, bad dns.
    NETWORK_ERROR: 'NETWORK_ERROR',      //--- Network not available.
    CANCEL_ERROR: 'CANCEL_ERROR'         //--- Request has been cancelled.
};

const buildErrorResponse = (error) => {
    const { response, request } = error;
    if (response) {
        let errorCode = 0;
        if (response.status >= 400 && response.status <= 499) {
            errorCode = ErrorCodes.CLIENT_ERROR;
        } else if (response.status >= 500 && response.status <= 599) {
            errorCode = ErrorCodes.SERVER_ERROR;
        }
        return { ok: false, data: response, status: response.status, errorCode };
    }
    if (request) {
        return { ok: false, data: null, errorCode: ErrorCodes.TIMEOUT_ERROR };
    }
    return { ok: false, data: null, errorCode: ErrorCodes.CONNECTION_ERROR };
};

class APIService {

    constructor() {
        this.apiName = "BackOfficeAPI";
        this.requestDefaultOptions = {};
    }

    async signUp(userData) {
        const settings = {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(userData),
        };
        try {
            const response = await fetch(`${URL_BASE}client/signup`, settings);
            return { ok: true, data: response.json(), status: response.status };
        } catch (e) {
            return buildErrorResponse(e);
        }
    }

    async registerAppointment(appointmentData) {
        let options = { body: appointmentData, ...this.requestDefaultOptions };
        try {
            const response = await API.post(this.apiName, '/client/appointments', options);
            return { ok: true, data: response, status: response.status };
        } catch (e) {
            return buildErrorResponse(e);
        }
    }

    async getDevices() {
        try {
            const response = await API.get(this.apiName, '/client/devices', this.requestDefaultOptions);
            return { ok: true, data: response, status: response.status }
        } catch (e) {
            return buildErrorResponse(e);
        }
    }

    async getIssuesByDevice(deviceId) {
        let options = Object.assign({
            headers: {
                'x-app-version': '5.0',
            }
        }, this.requestDefaultOptions);
        try {
            const response = await API.get(this.apiName, `/client/issues/${deviceId}`, options);
            return { ok: true, data: response, status: response.status };
        } catch (e) {
            return buildErrorResponse(e);
        }
    }

    async registerDevice(deviceToken) {
        let options = Object.assign({
            body: {
                firebase_device_token: deviceToken
            }
        }, this.requestDefaultOptions);
        try {
            const response = await API.post(this.apiName, '/client/register-device-data', options);
            return { ok: true, data: response };
        } catch (e) {
            return buildErrorResponse(e);
        }
    }

    async unregisterDevice(deviceToken) {
        let options = Object.assign({
            body: {
                firebase_device_token: deviceToken
            }
        }, this.requestDefaultOptions);
        return await API.post(this.apiName, '/client/unregister-device-data', options);
    }

    async getAllowedZipCodes() {
        return await API.get(this.apiName, '/client/profile/address/zip-codes', this.requestDefaultOptions);
    }

    async getAppointmentAvailability(zipCode, requestedTime) {
        try {
            const response = await API.get(this.apiName, `/client/check-availability/${zipCode}/${requestedTime}`, this.requestDefaultOptions);
            return { ok: true, data: response, status: response.status };
        } catch (e) {
            return buildErrorResponse(e);
        }
    }

    async getProfile() {
        try {
            const response = await API.get(this.apiName, '/client/profile', this.requestDefaultOptions);
            return { ok: true, data: response, status: response.status };
        } catch (e) {
            return buildErrorResponse(e);
        }
    }

    async updateProfile({ name, surnames, phone, email }) {
        let options = Object.assign({
            body: {
                first_name: name,
                last_name: surnames,
                phone_number: phone,
                email
            }
        }, this.requestDefaultOptions);

        try {
            const resp = await API.post(this.apiName, '/client/profile', options);
            return { ok: true, data: resp, status: resp.status };
        } catch (e) {
            return buildErrorResponse(e);
        }
    }

    async getPaymentCardList() {
        try {
            const response = await API.get(this.apiName, '/client/payment-methods', this.requestDefaultOptions);
            return { ok: true, data: response };
        } catch (e) {
            return buildErrorResponse(e);
        }
    }

    async registerPaymentCard(cardToken) {
        let options = Object.assign({
            body: {
                card_token: cardToken
            }
        }, this.requestDefaultOptions);
        return await API.post(this.apiName, '/client/payment-methods', options);
    }

    async deletePaymentCard(id) {
        try {
            const resp = await API.del(this.apiName, `/client/payment-methods/${id}`, this.requestDefaultOptions);
            return { ok: true, data: resp };
        } catch (e) {
            return buildErrorResponse(e);
        }
    }

    async getAddressList() {
        return await API.get(this.apiName, '/client/profile/address', this.requestDefaultOptions);
    }

    async geoCodeAddress(addressData) {
        let options = Object.assign({
            body: {
                address: addressData
            }
        }, this.requestDefaultOptions);
        return await API.post(this.apiName, '/client/profile/address/geocode', options);
    }

    async registerAddress(addressData, coordinates) {
        let options = Object.assign({
            body: {
                title: addressData.title,
                address: addressData.address,
                location: coordinates
            }
        }, this.requestDefaultOptions);
        return await API.post(this.apiName, '/client/profile/address', options);
    }

    async deleteAddress(id) {
        return await API.del(this.apiName, `/client/profile/address/${id}`, this.requestDefaultOptions);
    }



    async updateUserPassword(lastPassword, newPassword) {
        let options = Object.assign({
            body: {
                new_password: newPassword,
                old_password: lastPassword
            }
        }, this.requestDefaultOptions);
        try {
            const resp = await API.post(this.apiName, '/client/update-password', options);
            return { ok: true, data: resp, };
        } catch (e) {
            return buildErrorResponse(e);
        }
    }

    async getAppointmentList() {
        return await API.get(this.apiName, `/client/appointments?`, this.requestDefaultOptions);
    }

    async getAppointmentListHistory() {
        return await API.get(this.apiName, `/client/appointments?historical=1`, this.requestDefaultOptions);
    }

    async getAppConfig() {
        return await API.get(this.apiName, `/client/config`, this.requestDefaultOptions);
    }

    async sendMessage(message) {
        let options = Object.assign({
            body: {
                message: message
            }
        }, this.requestDefaultOptions);
        return await API.post(this.apiName, `/client/contact`, options);
    }

    async loadAptmInProgress() {
        try {
            const resp = await API.get(this.apiName, '/client/appointment-on-the-way', this.requestDefaultOptions);
            return { ok: true, data: resp }
        } catch (e) {
            return buildErrorResponse(e);
        }
    }

    async cancelAppointmentByCustomer(id) {
        try {
            const resp = await API.post(this.apiName, `/client/appointments/${id}/cancel`, this.requestDefaultOptions);
            return { ok: true, data: resp };
        } catch (e) {
            return buildErrorResponse(e);
        }

    }

    async getAppointmentToPay() {
        try {
            const response = await API.get(this.apiName, '/client/appointment-services-to-pay', this.requestDefaultOptions);
            return { ok: true, data: response };
        } catch (e) {
            return buildErrorResponse(e);
        }
    }


    async payAppointmentFinished(id, paymentData) {
        let options = Object.assign({
            body: {
                payment: paymentData
            }
        }, this.requestDefaultOptions);
        try {
            const resp = await API.post(this.apiName, `/client/appointments/${id}/pay-services`, options);
            return { ok: true, data: resp };
        } catch (e) {
            return buildErrorResponse(e);
        }
    }

    async recoverUserPassword(data) {
        let options = Object.assign({
            body: {
                ...data
            }
        }, this.requestDefaultOptions);
        try {
            const resp = await API.post(this.apiName, `/client/reset-password`, options);
            return { ok: resp.code !== 403, data: resp };
        } catch (e) {
            return buildErrorResponse(e);
        }
    }

    async requestRecoverUserPassword(emailP) {
        let options = Object.assign({
            body: {
                email: emailP,
            }
        }, this.requestDefaultOptions);
        try {
            const resp = await API.post(this.apiName, `/client/recover-password`, options);
            return { ok: resp.code !== 403, data: resp };
        } catch (e) {
            return buildErrorResponse(e);
        }
    }
}

export default new APIService();
